import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, OnDestroy, effect, inject, output } from '@angular/core';
import { model, input } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { DomPortal } from '@angular/cdk/portal';
import { ConfirmConfig } from '../confirm/confirm-config.interface';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { ConfirmDirective } from '../confirm/confirm.directive';
import { IconComponent } from '../icon/icon.component';

@Component({
    selector: 'ca-drawer',
    templateUrl: './drawer.component.html',
    styleUrls: ['./drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, MatIconButton, ConfirmDirective, IconComponent]
})
export class DrawerComponent implements OnDestroy {
    overriddenWidth = input<string>('');
    position = input<'left' | 'right' | 'bottom'>('right');
    bodyBordered = input<boolean>(false);
    bodyNoPaddings = input<boolean>(false);
    visible = model<boolean>(false);
    closeConfirm = input<ConfirmConfig>();
    closeConfirmed = output<void>();
    closeCancelled = output<void>();

    @HostBinding('class') get visibleClass() {
        return this.visible() ? 'visible' : 'hidden';
    }

    private overlay = inject(Overlay);
    private hostEl = inject(ElementRef<HTMLElement>);
    private overlayRef?: OverlayRef;
    private domPortal?: DomPortal<HTMLElement>;
    private visibleEffect = effect(() => {
        this.visible() ? this.attachToOverlay() : this.detachFromOverlay();
    });

    ngOnDestroy(): void {
        this.detachFromOverlay(true);
    }

    open() {
        this.visible.set(true);
    }

    close() {
        this.visible.set(false);
    }

    toggle() {
        this.visible.set(!this.visible());
    }

    tryToClose(): void {
        if (this.closeConfirm()?.disabled) {
            this.close();
        }
    }

    private attachToOverlay() {
        if (this.overlayRef?.hasAttached()) {
            return;
        }

        if (!this.overlayRef) {
            const positionStrategy = this.overlay.position().global();
            switch (this.position()) {
                case 'left':
                    positionStrategy.top('0').bottom('0').left('0');
                    break;
                case 'right':
                    positionStrategy.top('0').bottom('0').right('0');
                    break;
                case 'bottom':
                    positionStrategy.left('0').right('0').bottom('0');
                    break;
            }

            this.overlayRef = this.overlay.create({
                hasBackdrop: true,
                backdropClass: 'cdk-overlay-dark-backdrop',
                positionStrategy,
                scrollStrategy: this.overlay.scrollStrategies.block()
            });

            this.overlayRef.backdropClick().subscribe(() => this.close());
            this.overlayRef.detachments().subscribe(() => {
                if (this.visible()) {
                    this.visible.set(false);
                }
            });
        }

        this.domPortal = this.domPortal ?? new DomPortal(this.hostEl.nativeElement);
        this.overlayRef.attach(this.domPortal);
    }

    private detachFromOverlay(dispose = false) {
        if (!this.overlayRef) {
            return;
        }
        if (this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
        if (dispose) {
            this.overlayRef.dispose();
            this.overlayRef = undefined;
            this.domPortal = undefined;
        }
    }
}
